/**
 * The create modal's wiring: where the plan starts, the five answers of the details step, and the
 * one act at the end.
 *
 * ── The modal makes NO plan request, and that is still the whole architecture ────────────────
 * `POST /plan-template` refuses a plan without at least one real exercise — measured — and there is
 * no exercise picker here. The owner dropped autosave, so a plan does not exist on the server until
 * the trainer saves one (or, for a client, assigns it). This hook seeds a LOCAL draft (`id: null`)
 * into the document store and opens the builder on it. There is no mutation, no pending state and
 * no failure state for the act itself — the only thing that can go wrong at this point is the
 * trainer changing their mind, and Anuluj covers it.
 *
 * What it READS: the library (step 1's list), the chosen template's full read (the preview's pattern
 * week and the tree a copy is made of), and the trainer's own cover pool (`lib/useCoverPool`, which
 * also writes to that pool — a picture belongs to the trainer, not to the plan).
 *
 * ── The template's full read is SELECTED once, by a module-level function ────────────────────
 * `readPlanDocument` mints slot tokens, so calling it during render would hand every render a
 * different tree. A query's `select` is memoised on the function's identity and the data, and only
 * a module-level reference keeps the identity — see `templateCopySource`.
 *
 * ── Writing the store BEFORE navigating is deliberate ────────────────────────────────────────
 * `/plans/nowy` has nothing to fetch, so the document has to be there when it mounts. The builder's
 * own load effect fires on a new `select` result from the query cache and there is no query, so
 * this is the one path into the document for a plan that has never been stored. From here the
 * document store mirrors the seed to the device, so a reload of that route offers it back
 * (`entities/plan-draft/lib/unsavedPlanRecord.ts`); where the device kept nothing, the route says the
 * plan is gone rather than seeding an empty one the trainer did not ask for.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  copyTemplateDraft,
  coverKeyForCategory,
  createEmptyDraft,
  patternWeekOf,
  planCoverUrl,
  planLibraryQueryOptions,
  planTemplateFullQueryOptions,
  readPlanDocument,
  seedNewPlan,
  TEXT_LIMITS,
  type PatternWeekView,
  type PlanCategoryKey,
  type PlanDocumentRead,
  type PlanRecipient,
  type PlanSchedulingMode,
} from '~/entities/plan-draft';
import { useSessionUser } from '~/entities/session';
import type { TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse as PlanTemplateFullResponse } from '~/shared/api/generated/model';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import {
  INITIAL_CHOICES,
  copyChoices,
  createPlanRefusal,
  programRefusal,
  withCategory,
  withMode,
  type CreatePlanChoices,
  type CreatePlanStep,
  type TemplateReadState,
} from '../model/createPlanFlow';
import { coverSurvivesCategoryChange } from '../model/coverPool';
import {
  matchingTemplates,
  startSummary,
  templateChoices,
  type TemplateChoice,
  type TemplateFacet,
} from '../model/templatePicker';
import { useCoverPool, type CoverPool } from './useCoverPool';

/** What the preview and the copy both need out of one template's full read. */
interface TemplateCopySource {
  readonly read: PlanDocumentRead;
  readonly week: PatternWeekView;
}

/** The full read's `select`. Module-level, so its identity — and therefore its memo — holds. */
function templateCopySource(response: PlanTemplateFullResponse): TemplateCopySource {
  return { read: readPlanDocument(response), week: patternWeekOf(response) };
}

/** Step 1's list: its three drawn states, and what it holds. */
export interface TemplateListView {
  readonly status: 'pending' | 'error' | 'ready';
  /** What the facet and the search leave — the count and the no-match state read these alone. */
  readonly matches: readonly TemplateChoice[];
  /** The chosen template the filters leave out, drawn above them so the radio group keeps its check. */
  readonly pinned: TemplateChoice | null;
  /** Every template before the search and the facet — "nothing at all" reads unlike "nothing matches". */
  readonly total: number;
  readonly query: string;
  readonly setQuery: (query: string) => void;
  readonly facet: TemplateFacet;
  readonly setFacet: (facet: TemplateFacet) => void;
  readonly retry: () => void;
}

/** What step 1's right-hand half shows. */
export type ProgramPreview =
  | { readonly kind: 'scratch' }
  | {
      readonly kind: 'template';
      readonly choice: TemplateChoice;
      /** The pattern week's read: still open, failed, or answered. */
      readonly status: 'pending' | 'error' | 'ready';
      readonly week: PatternWeekView | null;
      readonly retry: () => void;
    };

export interface CreatePlanFlow {
  readonly step: CreatePlanStep;
  /** The client this plan is being built for, or `null` from the plan library. */
  readonly recipient: PlanRecipient | null;

  // ── Step 1 ──
  /** Where the plan starts: the library template it copies, or `null` for from nothing. */
  readonly templateId: number | null;
  readonly chooseStart: (templateId: number | null) => void;
  readonly templates: TemplateListView;
  readonly preview: ProgramPreview;
  /** Why „Dalej" cannot go on yet, already worded. `null` when it can. */
  readonly programRefusal: string | null;
  /** The chosen start in one line beside „Dalej" — „Pusty plan", or the template's name, length and system. */
  readonly startSummary: string | null;
  /** On to the details, prefilled from the template when the start changed. A no-op while refused. */
  readonly next: () => void;
  readonly back: () => void;

  // ── Step 2 ──
  readonly choices: CreatePlanChoices;
  /** The template a copy is made of, by name — the locked system and the notes say it. */
  readonly copyOf: string | null;
  /** How many characters of the name are used, and the ceiling. Tabular, as every figure is. */
  readonly nameCount: string;
  /** The cover row: the pool, its two writes, and the ceiling the server holds it against. */
  readonly covers: CoverPool;
  /** The picture the plan carries when no row is chosen — the category's own deterministic pick. */
  readonly autoCoverUrl: string;
  /** Why the details cannot be completed, already worded. `null` when they can. */
  readonly refusal: string | null;
  readonly setMode: (mode: PlanSchedulingMode) => void;
  readonly setCategory: (category: PlanCategoryKey) => void;
  readonly setName: (name: string) => void;
  readonly setDescription: (description: string) => void;
  readonly setCover: (coverId: number | null) => void;
  /** Seed the draft and open the builder on it. A no-op while the details are refused. */
  readonly create: () => void;
}

export function useCreatePlanFlow(
  onClose: () => void,
  recipient: PlanRecipient | null,
): CreatePlanFlow {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ownerId = useSessionUser()?.id ?? null;
  const contentLang = useContentLanguage();

  const [step, setStep] = useState<CreatePlanStep>('program');
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [facet, setFacet] = useState<TemplateFacet>('all');
  // The template that was chosen when the search or the facet last changed. The pinned slot follows
  // it rather than the live choice: picking a match below the pinned row must not unmount the row
  // above it, or the list jumps under the pointer and a second click lands on the wrong template.
  const [pinAnchor, setPinAnchor] = useState<number | null>(null);
  const [choices, setChoices] = useState<CreatePlanChoices>(INITIAL_CHOICES);
  // Which start the details were last prefilled FOR. Going back and forth on the same start keeps
  // what the trainer typed; choosing a different one replaces it with that start's own details.
  const [prefilledFor, setPrefilledFor] = useState<number | null>(null);

  const library = useQuery(planLibraryQueryOptions());
  const template = useQuery({
    ...planTemplateFullQueryOptions(templateId ?? 0, contentLang),
    enabled: templateId !== null,
    select: templateCopySource,
  });

  const covers = useCoverPool(
    choices.category,
    // An upload is a choice: the trainer went and picked that picture out of their own files.
    (coverId) => {
      setChoices((current) => ({ ...current, coverId }));
    },
    // The picture the plan was carrying is gone, so the plan falls back to „auto" — the same thing
    // the backend does to the plans that already used it.
    (coverId) => {
      setChoices((current) => (current.coverId === coverId ? { ...current, coverId: null } : current));
    },
  );

  const allTemplates = templateChoices(library.data, t);
  const chosen =
    templateId === null
      ? null
      : (allTemplates.find((candidate) => candidate.templateId === templateId) ?? null);

  // A disabled query reads `isPending` for ever, so the template id gates every reading of it.
  const readState: TemplateReadState =
    templateId === null || template.isPending
      ? { status: 'pending' }
      : template.data === undefined
        ? { status: 'error' }
        : { status: 'ready', read: template.data.read };
  const blocked = programRefusal(templateId, readState);
  const copyDraft = readState.status === 'ready' && readState.read.outcome === 'draft' ? readState.read.draft : null;

  const refusal = createPlanRefusal(choices);
  const retryTemplate = () => {
    void template.refetch();
  };

  return {
    step,
    recipient,

    templateId,
    chooseStart: setTemplateId,
    templates: {
      status: library.isPending ? 'pending' : library.isError ? 'error' : 'ready',
      ...matchingTemplates(allTemplates, query, facet, pinAnchor),
      total: allTemplates.length,
      query,
      setQuery: (next: string) => {
        setQuery(next);
        setPinAnchor(templateId);
      },
      facet,
      setFacet: (next: TemplateFacet) => {
        setFacet(next);
        setPinAnchor(templateId);
      },
      retry: () => {
        void library.refetch();
      },
    },
    preview:
      chosen === null
        ? { kind: 'scratch' }
        : {
            kind: 'template',
            choice: chosen,
            status: readState.status,
            week: template.data?.week ?? null,
            retry: retryTemplate,
          },
    programRefusal: blocked === null ? null : t(`web:planCreate.pick.refusal.${blocked}`),
    startSummary: startSummary(templateId, chosen, t),
    next: () => {
      if (blocked !== null) return;
      if (templateId !== prefilledFor) {
        setChoices(copyDraft === null ? INITIAL_CHOICES : copyChoices(copyDraft, recipient, t));
        setPrefilledFor(templateId);
      }
      setStep('details');
    },
    back: () => {
      setStep('program');
    },

    choices,
    copyOf: copyDraft?.name ?? chosen?.name ?? null,
    // The ceiling is the wire's own, read from the one place it is stated.
    nameCount: `${String(choices.name.length)} / ${String(TEXT_LIMITS.planName.max)}`,
    covers,
    // Previewed from the SAME two inputs the seed derives it from, so the picture shown for „auto"
    // and the picture on the created plan cannot disagree.
    autoCoverUrl: planCoverUrl(
      coverKeyForCategory(choices.category, choices.name),
      choices.category,
      choices.name,
      'thumb',
    ),
    refusal: refusal === null ? null : t(`web:planCreate.refusal.${refusal}`),
    // Through `withMode`, which refuses on a copy: the copy's system is its template's (R1).
    setMode: (mode) => {
      setChoices((current) => withMode(current, templateId !== null, mode));
    },
    // Through `withCategory`, which drops a CURATED cover chosen from the old category's pool and
    // keeps one the trainer uploaded — an own cover fits every category. See both.
    setCategory: (category) => {
      setChoices((current) =>
        withCategory(current, category, coverSurvivesCategoryChange(covers.isOwn(current.coverId))),
      );
    },
    setName: (name) => {
      setChoices((current) => ({ ...current, name }));
    },
    setDescription: (description) => {
      setChoices((current) => ({ ...current, description }));
    },
    setCover: (coverId) => {
      setChoices((current) => ({ ...current, coverId }));
    },
    create: () => {
      if (refusal !== null) return;
      const name = choices.name.trim();
      // Every seed goes through `seedNewPlan`, never a bare store write: it binds the recipient,
      // names the plan's owner, mints its document key and writes the device's first copy at once,
      // so a reload in the next breath already finds it — with its client.
      if (templateId !== null) {
        // The details step is only reachable once the read answered with a draft (`next`).
        if (copyDraft === null) return;
        seedNewPlan(
          copyTemplateDraft(copyDraft, templateId, {
            name,
            description: choices.description,
            category: choices.category,
            coverId: choices.coverId,
          }),
          recipient,
          ownerId,
        );
      } else {
        seedNewPlan(
          createEmptyDraft({
            name,
            category: choices.category,
            mode: choices.mode,
            description: choices.description,
            ...(choices.coverId === null ? {} : { coverId: choices.coverId }),
            // DATA, so it is resolved here rather than in `model/` — the entity holds no sentences.
            phaseName: t('web:planCreate.firstPhaseName'),
          }),
          recipient,
          ownerId,
        );
      }
      // The modal closes first, so the builder mounts with no dialog above it and the shell's
      // focus move lands on the new screen's heading rather than inside a popup that is unmounting.
      onClose();
      void navigate({ to: '/plans/nowy' });
    },
  };
}
